import { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";

const OTP_SHOP_IDS = [
  "JS",
  "GMP",
  "JCPF",
  "JCHT",
  "MDYHT",
  "MDYPF",
  "MT",
  "OSPF",
  "OSHT",
  "DNGHT",
  "DNGPF",
  "AYMHT",
  "AYMPF",
];

const OTP_STORAGE_KEY = "voucherEditIssuedOtpMap";

function cleanText(value) {
  return String(value ?? "").trim();
}

function rowId(row) {
  return cleanText(row?.id || row?._key || row?.requestId);
}

function rowShop(row) {
  return cleanText(row?.shopCode || row?.shop_code || row?.shop);
}

function rowVoucher(row) {
  return cleanText(row?.voucherNo || row?.voucher_no);
}

function rowStatus(row) {
  return cleanText(row?.status || "REQUESTED").toUpperCase();
}

function readPayload(row) {
  const payload = row?.payload || row?.meta || {};

  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }

  return payload || {};
}

function rowItem(row) {
  const payload = readPayload(row);
  return payload?.item || payload?.oldItem || row?.item || {};
}

function formatDateTime(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString();
}

function readOtpStorage() {
  try {
    const raw = localStorage.getItem(OTP_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};

    const now = Date.now();
    const cleaned = {};

    Object.entries(parsed || {}).forEach(([id, row]) => {
      if (!row?.otp) return;
      if (row.expiresAt && Number(row.expiresAt) < now) return;
      cleaned[id] = row;
    });

    localStorage.setItem(OTP_STORAGE_KEY, JSON.stringify(cleaned));
    return cleaned;
  } catch {
    return {};
  }
}

function writeOtpStorage(map) {
  try {
    localStorage.setItem(OTP_STORAGE_KEY, JSON.stringify(map || {}));
  } catch {
    // ignore localStorage failure
  }
}

function getOtpFromMap(map, id, requestRow) {
  const row = map?.[id];
  if (!row?.otp) return "";

  if (row.expiresAt && Number(row.expiresAt) < Date.now()) {
    return "";
  }

  const issuedRaw =
    requestRow?.otpIssuedAt ||
    requestRow?.otp_issued_at ||
    requestRow?.updatedAt ||
    requestRow?.updated_at;

  const issuedAt = issuedRaw ? new Date(issuedRaw).getTime() : 0;
  const savedAt = Number(row.savedAt || 0);

  // If dashboard local OTP was saved before latest issue time, it is stale.
  if (issuedAt && savedAt && savedAt < issuedAt) {
    return "";
  }

  return row.otp;
}

export default function VoucherEditOtpCard() {
  const [rows, setRows] = useState([]);
  const [issuedOtpMap, setIssuedOtpMap] = useState(() => readOtpStorage());
  const [loading, setLoading] = useState(false);
  const [issuingId, setIssuingId] = useState("");
  const [error, setError] = useState("");

  function saveIssuedOtp(id, otp, row, apiData) {
    if (!id || !otp) return;

    const expiresRaw =
      apiData?.request?.otpExpiresAt ||
      apiData?.request?.otp_expires_at ||
      apiData?.otpExpiresAt ||
      apiData?.otp_expires_at ||
      row?.otpExpiresAt ||
      row?.otp_expires_at;

    const expiresAt = expiresRaw
      ? new Date(expiresRaw).getTime()
      : Date.now() + 5 * 60 * 1000;

    setIssuedOtpMap((prev) => {
      const next = {
        ...prev,
        [id]: {
          otp,
          expiresAt,
          savedAt: Date.now(),
        },
      };

      writeOtpStorage(next);
      return next;
    });
  }

  async function loadRequests() {
    try {
      setLoading(true);
      setError("");

      const results = await Promise.all(
        OTP_SHOP_IDS.map(async (shop) => {
          try {
            const res = await fetch(
              API_BASE + "/api/erp/voucher-edit/otp-requests/" + encodeURIComponent(shop)
            );

            const data = await res.json().catch(() => ({}));

            if (!res.ok || data.success === false) {
              console.warn("Voucher edit OTP load failed for shop:", shop, data);
              return [];
            }

            const shopRows = Array.isArray(data.rows)
              ? data.rows
              : Array.isArray(data.requests)
                ? data.requests
                : Array.isArray(data.data)
                  ? data.data
                  : [];

            return shopRows.map((row) => ({
              ...row,
              shopCode: row.shopCode || row.shop_code || row.shop || shop,
            }));
          } catch (err) {
            console.warn("Voucher edit OTP load error for shop:", shop, err);
            return [];
          }
        })
      );

      const nextRows = results
        .flat()
        .sort((a, b) => {
          const ad = new Date(a.requestedAt || a.requested_at || a.createdAt || a.created_at || 0).getTime();
          const bd = new Date(b.requestedAt || b.requested_at || b.createdAt || b.created_at || 0).getTime();
          return bd - ad;
        });

      setRows(nextRows);
      setIssuedOtpMap(readOtpStorage());
    } catch (err) {
      console.error("Voucher edit OTP list failed:", err);
      setError(err.message || "Failed to load voucher edit requests");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function issueOtp(row) {
    const id = rowId(row);

    if (!id) {
      alert("Request ID မတွေ့ပါ");
      return;
    }

    try {
      setIssuingId(id);
      setError("");

      const res = await fetch(
        API_BASE + "/api/erp/voucher-edit/issue-otp/" + encodeURIComponent(id),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            issuedBy: "erp_dashboard",
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      console.log("Voucher edit OTP issue response:", data);

      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || "OTP issue failed");
      }

      if (!data.otp) {
        throw new Error("VPS did not return OTP. Check /issue-otp API response.");
      }

      saveIssuedOtp(id, data.otp, row, data);

      await loadRequests();

      alert("OTP issued: " + data.otp);
    } catch (err) {
      console.error("Voucher edit OTP issue failed:", err);
      alert("OTP issue failed: " + err.message);
    } finally {
      setIssuingId("");
    }
  }

  useEffect(() => {
    loadRequests();

    // OTP မပျောက်စေချင်လို့ auto refresh ကို 60 sec ထားထားတယ်။
    const timer = window.setInterval(loadRequests, 60000);
    return () => window.clearInterval(timer);
  }, []);

  const activeRows = useMemo(() => {
    const closed = new Set(["APPLIED", "CLOSED", "USED", "REJECTED", "CANCELLED"]);

    return rows
      .filter((row) => !closed.has(rowStatus(row)))
      .slice(0, 8);
  }, [rows]);

  return (
    <section className="voucherOtpCard">
      <div className="voucherOtpCard__head">
        <div>
          <div className="voucherOtpCard__tag">POS → VPS → PostgreSQL</div>
          <h2>Voucher Edit OTP Requests</h2>
          <p>Daily Sale Report မှ edit request တင်လာရင် ဒီ card ထဲမှာ OTP issue လုပ်ရန်။</p>
        </div>

        <div className="voucherOtpCard__actions">
          <span className="voucherOtpCard__count">{activeRows.length} pending</span>
          <button type="button" onClick={loadRequests} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="voucherOtpCard__error">{error}</div>}

      <div className="voucherOtpCard__tableWrap">
        <table className="voucherOtpCard__table">
          <thead>
            <tr>
              <th>Shop</th>
              <th>Voucher</th>
              <th>Code</th>
              <th>Color</th>
              <th>Size</th>
              <th>Status</th>
              <th>Requested</th>
              <th>OTP / Action</th>
            </tr>
          </thead>

          <tbody>
            {activeRows.length === 0 ? (
              <tr>
                <td colSpan="8" className="voucherOtpCard__empty">
                  No pending voucher edit request
                </td>
              </tr>
            ) : (
              activeRows.map((row) => {
                const id = rowId(row);
                const status = rowStatus(row);
                const item = rowItem(row);
                const visibleOtp = getOtpFromMap(issuedOtpMap, id, row);
                const statusClass =
                  "voucherOtpStatus voucherOtpStatus--" +
                  status.toLowerCase().replace(/[^a-z0-9]+/g, "-");

                return (
                  <tr key={id || rowVoucher(row)}>
                    <td><span className="voucherOtpShop">{rowShop(row) || "-"}</span></td>
                    <td>{rowVoucher(row) || "-"}</td>
                    <td>{item?.code || item?.product_code || item?.productCode || "-"}</td>
                    <td>{item?.color || "-"}</td>
                    <td>{item?.size || "-"}</td>
                    <td><span className={statusClass}>{status}</span></td>
                    <td>{formatDateTime(row.requestedAt || row.requested_at || row.createdAt || row.created_at)}</td>
                    <td>
                      {visibleOtp ? (
                        <div className="voucherOtpBox">
                          <strong>{visibleOtp}</strong>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(visibleOtp)}
                          >
                            Copy
                          </button>
                        </div>
                      ) : status === "VERIFIED" ? (
                        <span className="voucherOtpDone">POS verified</span>
                      ) : (
                        <button
                          type="button"
                          className="voucherOtpIssueBtn"
                          onClick={() => issueOtp(row)}
                          disabled={issuingId === id}
                        >
                          {issuingId === id
                            ? "Issuing..."
                            : status.includes("ISSUED")
                              ? "Re-Issue OTP"
                              : "Issue OTP"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
